<template>
  <el-dialog v-loading="loading" :title="title" :visible.sync="dialogVisible" top="8vh" width="600px"
    element-loading-text="加载中" @close="handleClose">
    <el-form v-if="dialogVisible" ref="formRef" :model="form" :rules="rules" label-width="120px">
      <!-- 退款方式：全部退款 / 部分退款 -->
      <el-form-item label="退款方式">
        <el-radio-group v-model="form.refund_type" @change="handleRefundTypeChange">
          <el-radio :label="0">全部退款</el-radio>
          <el-radio :label="1" :disabled="!canPartialRefund">部分退款</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 退款商品列表 —— 全部退款（只读展示） -->
      <el-form-item v-if="isGoodsOrder && goodsList.length && !isPartialRefund" label="退款商品">
        <div v-for="(item, index) in goodsList" :key="index" class="refund-goods-item">
          <img :src="item.pic" alt="" class="goods-img" />
          <div class="goods-info">
            <span>规格：{{ item.sku }}</span>
            <span>数量：{{ item.maxNum }}</span>
            <span>价格：{{ item.amount }}</span>
          </div>
        </div>
      </el-form-item>

      <!-- 退款商品列表 —— 部分退款（可勾选 + 选择数量） -->
      <el-form-item v-if="canPartialRefund && isPartialRefund" label="选择商品" prop="selectedGoods">
        <div class="refund-goods-select">
          <div class="select-header">
            <el-checkbox :value="isAllChecked" @change="handleCheckAll">全选</el-checkbox>
            <span class="selected-tip">已选 {{ selectedGoods.length }} 件</span>
          </div>
          <div v-for="(item, index) in goodsList" :key="index" class="refund-goods-item"
            :class="{ 'is-disabled': !item.checked }">
            <el-checkbox v-model="item.checked" @change="handleGoodsCheckChange" />
            <img :src="item.pic" alt="" class="goods-img goods-img--small" />
            <div class="goods-info">
              <span>规格：{{ item.sku }}</span>
              <span>价格：{{ item.amount }}</span>
              <span>可退数量：{{ item.maxNum }}</span>
            </div>
            <div class="goods-num">
              <span>退款数量</span>
              <el-input-number v-model="item.refundNum" size="small" controls-position="right" :min="1"
                :max="item.maxNum" :disabled="!item.checked" @change="handleNumChange" />
            </div>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="订单总金额">
        <el-input :value="form.total_price || '0.00'" disabled />
      </el-form-item>
      <el-form-item label="可退运费">
        <el-input :value="form.refund_postage || '0.00'" disabled />
      </el-form-item>
      <el-form-item label="可退商品">
        <el-input :value="form.refund_goods_price || '0.00'" disabled />
      </el-form-item>
      <el-form-item label="可退税金">
        <el-input :value="form.refund_tax || '0.00'" disabled />
      </el-form-item>
      <!-- 退款原因 -->
      <el-form-item label="退款原因" prop="remark">
        <el-input v-model="form.remark" type="textarea" :rows="2" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">返回</el-button>
      <el-button type="primary" :loading="loading" @click="handleRefundAgree">退款同意</el-button>
    </template>
  </el-dialog>
</template>

<script>
import {
  goodsOrderDetail,
  refund_amount_detail,
  buy_order_apply_refund_detail,
  GoodsOrderRefundPart,  // 部分退款
  GoodsOrderRefundAll    // 全部退款
} from '@/api/xgcApi'

// 订单类型常量
const ORDER_TYPE = {
  GOODS: 5, // 商品订单
  BUY: 3,   // 代买
  PAY: 4    // 代付
}

export default {
  name: 'RefundDialog',
  data() {
    return {
      dialogVisible: false,
      loading: false,
      calcTimer: null,
      currentRow: null,
      goodsList: [],
      form: {
        id: '',
        refund_type: 0, // 0-全额 1-部分
        refund_amount: null,
        refund_points: null,
        total_price: '',
coupon_money:'',
refund_goods_price:'',
refund_postage:'',
refund_tax:'',

        user_balance: '0.00',
        price: '0.00',
        remark: '',
        goods_order_apply: [],
        selectedGoods: [],
        _order_price: 0,
        _refunded_price: 0,
        _deduction_points: 0,
        _refunded_points: 0,
        _coupon_money: 0,
        _refund_goods_price: 0,
        _refund_postage: 0,
        _refund_tax: 0
      },
      orderType: ORDER_TYPE.GOODS,
      rules: {
        remark: [
          { required: true, message: '请输入退款原因', trigger: 'change' }
        ],
        selectedGoods: [
          {
            validator: (rule, value, callback) => {
              if (!this.isGoodsOrder || !this.isPartialRefund || !this.goodsList.length) {
                callback()
                return
              }
              const selected = this.goodsList.filter(item => item.checked)
              if (!selected.length) {
                callback(new Error('请至少选择一件退款商品'))
                return
              }
              if (selected.some(item => !item.refundNum || item.refundNum < 1)) {
                callback(new Error('请填写正确的退款数量'))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  computed: {
    title() {
      const map = {
        [ORDER_TYPE.GOODS]: '商品退款',
        [ORDER_TYPE.BUY]: '代买退款',
        [ORDER_TYPE.PAY]: '代付退款'
      }
      return map[this.orderType] || '申请退款'
    },
    isGoodsOrder() {
      return this.orderType === ORDER_TYPE.GOODS
    },
    isPartialRefund() {
      return this.form.refund_type === 1
    },
    canPartialRefund() {
      return this.isGoodsOrder && this.goodsList.length > 0
    },
    isAllChecked() {
      return this.goodsList.length > 0 && this.goodsList.every(item => item.checked)
    },
    selectedGoods() {
      return this.goodsList.filter(item => item.checked)
    },
    maxRefundAmount() {
      const max = Number(this.form.total_price)
      return max > 0 ? max.toFixed(2) : '0.00'
    },
    maxRefundPoints() {
      const max = Number(this.form._deduction_points) - Number(this.form._refunded_points)
      return max > 0 ? max : 0
    }
  },
  beforeDestroy() {
    if (this.calcTimer) {
      clearTimeout(this.calcTimer)
      this.calcTimer = null
    }
  },
  methods: {
    open(row, type) {
      this.orderType = type || ORDER_TYPE.GOODS
      this.currentRow = row || {}
      this.goodsList = []
      this.form = {
        ...this.form,
        id: row?.id || '',
        refund_type: 0,
        remark: '',
        selectedGoods: []
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate()
      })
      this.loadDetail()
    },

    async loadDetail() {
      if (!this.form.id) return
      this.loading = true
      try {
        const api = this.isGoodsOrder ? goodsOrderDetail : buy_order_apply_refund_detail
        const res = await api({ id: this.form.id })
        const data = res.data || {}

        this.form = {
          ...this.form,
          ...data,
          remark: this.form.remark,
          refund_type: 0,
          selectedGoods: [],
          _order_price: data.price || 0,
          _refunded_price: data.refund_price || 0,
          _deduction_points: data.deduction_points || 0,
          _refunded_points: data.refund_points || 0,
          _coupon_money: data.coupon_money || 0,
          _refund_goods_price: data.refund_goods_price || 0,
          _refund_postage: data.refund_postage || 0,
          _refund_tax: data.refund_tax || 0,
          refund_amount: null,
          refund_points: null
        }

        this.goodsList = this.buildGoodsList(this.currentRow, data)

        if (this.goodsList.length) {
          this.calcRefundAmount()
        }
      } catch (error) {
        console.error('获取退款详情失败:', error)
        // this.$message.error('获取退款详情失败')
      } finally {
        this.loading = false
      }
    },

    buildGoodsList(row, detail) {
      const specList = (detail && detail.spec) || (row && row.goods_order_spec) || []

      return specList
        .map(item => {
          let goodsData = {}
          try {
            goodsData = item.goods_data ? JSON.parse(item.goods_data) : {}
          } catch (e) {
            goodsData = {}
          }

          const num = Number(item.num) || 0
          const refunded = Number(item.refund_num) || 0
          const maxNum = Number(
            item.remaining_num != null
              ? item.remaining_num
              : Math.max(0, num - refunded)
          )

          const firstPic = (goodsData.pic || '').split(',')[0]

          return {
            id: item.id,
            maxNum,
            pic: item.pic || firstPic || '',
            sku: item.sku || '',
            amount: item.price != null ? item.price : '0.00',
            checked: true,
            refundNum: maxNum
          }
        })
        .filter(item => item.id != null && item.maxNum > 0)
    },

    calcRefundAmount() {
      if (this.calcTimer) {
        clearTimeout(this.calcTimer)
        this.calcTimer = null
      }

      this.calcTimer = setTimeout(async () => {
        if (!this.form.id) return
        if (!this.goodsList.length) return

        let skuList = ''
        if (this.isPartialRefund) {
          skuList = this.goodsList
            .filter(item => item.checked && Number(item.refundNum) > 0)
            .map(item => `${item.id}:${item.refundNum}`)
            .join(',')
        } else {
          skuList = this.goodsList
            .map(item => `${item.id}:${item.maxNum}`)
            .join(',')
        }

        if (!skuList) return

        try {
          const res = await refund_amount_detail({
            goods_order_id: this.form.id,
            goods_order_sku_list: skuList
          })
          const data = (res && res.data) || {}

          if (data.total_price != null) this.form.total_price = data.total_price
          if (data.refund_postage != null) this.form.refund_postage = data.refund_postage
          if (data.refund_goods_price != null) this.form.refund_goods_price = data.refund_goods_price
          if (data.coupon_money != null) this.form.coupon_money = data.coupon_money
          if (data.refund_tax != null) this.form.refund_tax = data.refund_tax






          if (data.user_balance != null) this.form.user_balance = data.user_balance
          if (data.price != null) this.form.price = data.price
          if (data.refund_price != null) this.form._refunded_price = data.refund_price
          if (data.refund_amount != null) this.form.refund_amount = data.refund_amount
          if (data.refund_points != null) this.form.refund_points = data.refund_points
        } catch (error) {
          console.error('计算退款金额失败:', error)
        }
      }, 300)
    },

    handleRefundTypeChange(val) {
      if (val === 0) {
        this.goodsList.forEach(item => {
          item.checked = true
          item.refundNum = item.maxNum
        })
      }
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate('selectedGoods')
      })
      this.calcRefundAmount()
    },

    handleCheckAll(val) {
      this.goodsList.forEach(item => {
        item.checked = val
        if (val) {
          item.refundNum = item.refundNum || item.maxNum
        }
      })
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('selectedGoods')
      })
      this.calcRefundAmount()
    },

    handleGoodsCheckChange() {
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('selectedGoods')
      })
      this.calcRefundAmount()
    },

    handleNumChange() {
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('selectedGoods')
      })
      this.calcRefundAmount()
    },

    /**
     * 构建 order_goods_spec_list 字符串 "specId:num,specId:num"
     */
    buildOrderGoodsSpecList() {
      if (this.isPartialRefund && this.goodsList.length) {
        return this.goodsList
          .filter(item => item.checked && Number(item.refundNum) > 0)
          .map(item => `${item.id}:${item.refundNum}`)
          .join(',')
      }
      if (this.goodsList.length) {
        return this.goodsList
          .map(item => `${item.id}:${item.maxNum}`)
          .join(',')
      }
      const specList = (this.currentRow && this.currentRow.goods_order_spec) || []
      return specList.map(item => `${item.id}:${item.num}`).join(',')
    },

    // 退款同意
    async handleRefundAgree() {
      // 1. 表单校验
      try {
        await this.$refs.formRef?.validate()
      } catch (error) {
        return
      }

      // 2. 二次确认
      try {
        await this.$confirm('确定要对此订单进行退款吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (cancel) {
        return
      }

      // 3. 提交退款
      this.loading = true
      try {
        let res
        if (this.isPartialRefund) {
          // 部分退款：需要传 order_goods_spec_list
          res = await GoodsOrderRefundPart({
            id: this.form.id,
            apply_reason: this.form.remark,
            order_goods_spec_list: this.buildOrderGoodsSpecList()
          })
        } else {
          // 全部退款：只需 id + apply_reason
          res = await GoodsOrderRefundAll({
            id: this.form.id,
            apply_reason: this.form.remark
          })
        }
        this.$message({
          message: res?.msg || '退款成功',
          type: 'success'
        })
        this.dialogVisible = false
        this.$emit('change')
      } catch (error) {
        // console.error('退款失败:', error)
        // this.$message.error('退款失败，请重试')
      } finally {
        this.loading = false
      }
    },

    // 关闭弹窗
    handleClose() {
      if (this.calcTimer) {
        clearTimeout(this.calcTimer)
        this.calcTimer = null
      }
      this.dialogVisible = false
      this.$refs.formRef?.resetFields()
      this.goodsList = []
      this.currentRow = null
      this.form = {
        ...this.form,
        refund_type: 0,
        refund_amount: null,
        refund_points: null,
        remark: '',
        selectedGoods: []
      }
    }
  }
}
</script>

<style scoped>
.refund-goods-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.refund-goods-item.is-disabled {
  opacity: 0.6;
  background-color: #fafafa;
}

.goods-img {
  width: 72px;
  height: 72px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.goods-img--small {
  width: 56px;
  height: 56px;
}

.goods-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #333;
  min-width: 0;
}

.goods-info span {
  line-height: 1.5;
  word-break: break-all;
}

.refund-goods-select {
  width: 100%;
}

.select-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px dashed #ebeef5;
}

.selected-tip {
  font-size: 12px;
  color: #909399;
}

.goods-num {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-size: 12px;
  color: #909399;
}

.goods-num :deep(.el-input-number) {
  width: 110px;
}

.form-tip {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

:deep(.el-dialog__body) {
  padding: 20px 30px;
}

:deep(.el-form-item) {
  margin-bottom: 18px;
}

:deep(.el-input.is-disabled .el-input__inner) {
  color: #333;
  background-color: #f5f7fa;
}
</style>